import { useEffect } from "react";
import { useLanguage } from "../i18n/lang.ts";
import { vi } from "../i18n/vi.ts";
import { AvatarPick, Splash } from "../ui/screens/Intro.tsx";
import { CityHud, HouseInfo, HouseLobby } from "../ui/screens/City.tsx";
import { Achievements, AchievementPopup } from "../ui/screens/Achievements.tsx";
import { InstallGuide, Preloader } from "../ui/screens/Install.tsx";
import { Leaderboard } from "../ui/screens/Leaderboard.tsx";
import { PurchaseReveal } from "../ui/screens/PurchaseReveal.tsx";
import { GetApp } from "../ui/screens/Updates.tsx";
import { FxLayer } from "../ui/fx/FxLayer.tsx";
import { OutOfMoves, PauseMenu, PuzzleHud, ResultScreen, TutorialBubble } from "../ui/screens/Puzzle.tsx";
import { Settings } from "../ui/screens/Settings.tsx";
import { Shop } from "../ui/screens/Shop.tsx";
import { Button } from "../ui/components/common.tsx";
import { Companion } from "../ui/companion/Companion.tsx";
import { bootApp } from "./actions.ts";
import { GameHost } from "./GameHost.tsx";
import { shallowEqual, useApp } from "./store.ts";

let booted = false;

export function App() {
  useEffect(() => {
    if (booted) return;
    booted = true;
    void bootApp();
  }, []);
  // Copy tables switch language in place; re-rendering from the root shows it everywhere at once.
  useLanguage();
  const ui = useApp(
    (s) => ({
      loaded: s.save !== null,
      screen: s.ui.screen,
      overlay: s.ui.overlay,
      lobby: s.ui.lobbyHouse,
      info: s.ui.infoHouse,
      cityReady: s.ui.cityReady,
      paused: s.ui.paused,
      result: s.ui.result !== null,
      outOfMoves: s.ui.outOfMoves,
      tutorial: s.ui.tutorial !== null,
      attempt: s.ui.puzzle?.attempt ?? 0,
      toast: s.ui.toast,
      contextLost: s.ui.contextLost,
      installGuide: s.ui.installGuide,
      preloading: s.ui.preload !== null,
      reveal: s.ui.purchaseReveal !== null,
    }),
    shallowEqual,
  );

  return (
    <div className="app">
      {/* iOS downloads everything first, so the engine waits for the preloader. */}
      {ui.loaded && !ui.installGuide && !ui.preloading ? <GameHost /> : null}
      {ui.screen === "boot" || (ui.screen === "city" && !ui.cityReady) ? <Splash /> : null}
      {ui.screen === "pick" ? <AvatarPick mode="first" /> : null}
      {ui.screen === "city" && ui.cityReady ? <CityHud /> : null}
      {ui.screen === "puzzle" ? <PuzzleHud /> : null}
      {ui.screen === "puzzle" ? <Companion /> : null}
      {ui.screen === "puzzle" && ui.tutorial ? <TutorialBubble key={`tutorial-${ui.attempt}`} /> : null}
      {ui.screen === "puzzle" && ui.paused ? <PauseMenu /> : null}
      {ui.screen === "puzzle" && ui.outOfMoves ? <OutOfMoves /> : null}
      {ui.screen === "puzzle" && ui.result ? <ResultScreen key={`result-${ui.attempt}`} /> : null}
      {ui.lobby ? <HouseLobby houseId={ui.lobby} /> : null}
      {ui.info ? <HouseInfo houseId={ui.info} /> : null}
      {ui.overlay === "shop" ? <Shop mode="shop" /> : null}
      {ui.overlay === "wardrobe" ? <Shop mode="wardrobe" /> : null}
      {ui.overlay === "leaderboard" ? <Leaderboard /> : null}
      {ui.overlay === "settings" ? <Settings /> : null}
      {ui.overlay === "pick-change" ? <AvatarPick mode="change" /> : null}
      {ui.overlay === "achievements" ? <Achievements /> : null}
      {ui.overlay === "get-app" ? <GetApp /> : null}
      {ui.reveal ? <PurchaseReveal /> : null}
      <AchievementPopup />
      {ui.preloading ? <Preloader /> : null}
      {ui.installGuide ? <InstallGuide /> : null}
      <FxLayer />
      {ui.toast ? (
        // Keys share one parent with the result card's: prefixed so toast 1 never collides with attempt 1.
        <div className="toast" role="status" key={`toast-${ui.toast.id}`} data-testid="toast">
          {ui.toast.text}
        </div>
      ) : null}
      {ui.contextLost ? (
        <div className="fatal">
          <p>{vi.contextLost}</p>
          <Button onClick={() => window.location.reload()}>{vi.reload}</Button>
        </div>
      ) : null}
      <div className="rotate" aria-hidden="true">
        <p>{vi.rotate}</p>
      </div>
    </div>
  );
}
